import { isEqual, uniq } from "lodash";
import moment from "moment";
import { Component, Fragment, JSX } from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import { ExportType } from "@app/exports/enumerations";
import { ProjectPerspective } from "@app/projects";
import { RegionGranularity } from "@app/region-sets";
import { ProgressBar, TooltipWrapper } from "@app/shared";
import { PrimaryTable as SkeletonTable } from "@app/shared/skeleton-components";
import { AdequacyAggregate, getTimeString } from "./";

import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { ColumnConfig, ColumnFilter, ColumnGroup, genericFilter, numberSort, parseNumber, printNumberAsString, SortDirections, TextAlignment, Waffles, WafflesFilterType } from "@app/shared/waffles";
import { TemplateCategory, TemplateService } from "@app/templates";
import { Roles, UserContextProps, withUserContext } from "../../../user";
import { NetworkAssessmentAnalysisType, ProjectRunConfigurationStatus, ProjectRunConfigurationType, ProviderNetworkType, RegionSetStatusType } from "../../enumerators";
import { CountyProjectContextProps, withCountyProjectContext } from "../../CountyProjectContext";
import { ChangeStatusLink } from "../ChangeStatusLink";
import InformationalIndicator from "../InformationalSpecialtyGroupIndicator";
import { cmsCreditDataAlert, cmsCreditIconToggleApply, cmsCreditIconToggleChanges } from "./CMSCreditComponents";
import { Features } from "@app/feature-flags";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { AdequacyProgressBarLabels } from "@app/shared/progressBarBand";
import { querystringify, parseQuerystring } from "@app/shared/utility";
import { showPatternOfCare } from "./utility";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";

type AdequacyAggregateDetailsProps =
  RouteComponentProps<unknown, unknown, ProjectRouteParams> &
  CountyProjectContextProps &
  UserContextProps &
  FeatureFlagContextProps &
  {
    countyPerspectiveOnlyItems: boolean;
    exportComparedRunConfigurationId?: number;
    exportEndpoint: string;
  };

class AdequacyAggregateWaffleToppings {
  public accessMet: string;
  public accessNotMet: string;
  public accessTotal: string;
  public adequacyGraph: JSX.Element;
  public adequacyIndex: string;
  public attestationPercent: string;
  public countySSA: string;
  public countyClass: string;
  public exceptionsApproved: JSX.Element;
  public exceptionsTotal: string;
  public fourStarsGreater: string;
  public gapsFillable: string;
  public gapsFillableBySingleProvider: string;
  public gapsFillableByMultipleProviders: string;
  public gapsTotal: JSX.Element;
  public isTelehealth: string;
  public key: string;
  public lessThanFourStars: string;
  public fullMemberCountWithAccess: string;
  public fullMemberCountWithoutAccess: string;
  public fullMembershipCount: string;
  public membershipName: JSX.Element;
  public penetration: string;
  public resultsFor: string;
  public servicingMet: string;
  public servicingNotMet: string;
  public servicingTotal: string;
  public membershipRisk: string;
  public membershipRiskPercent: string;
  public regionStatus: JSX.Element;
  public title: JSX.Element;

  constructor(key: string, resultsFor: string) {
    /* eslint-disable sort-keys */
    // The order of these property assignments must match the order of the columns
    // in AdequacyAggregateDetails.getColumnConfig() for compare mode to display properly.
    this.key = key;
    this.title = null;
    this.regionStatus = null;
    this.membershipName = null;
    this.fullMembershipCount = "---";
    this.resultsFor = resultsFor;
    this.countySSA = "---";
    this.countyClass = "---";
    this.adequacyIndex = "---";
    this.adequacyGraph = (
      <div style={{ minWidth: 200 }}>
        <ProgressBar
          bands={[]}
          isGrayscale={true}
        />
      </div>
    );
    this.gapsTotal = <span key={0}>---</span>;
    this.gapsFillable = null;
    this.gapsFillableBySingleProvider = null;
    this.gapsFillableByMultipleProviders = null;
    this.exceptionsTotal = null;
    this.exceptionsApproved = <span key={0}>---</span>;
    this.fullMemberCountWithAccess = "---";
    this.fullMemberCountWithoutAccess = "---";
    this.accessMet = null;
    this.accessNotMet = null;
    this.accessTotal = null;
    this.isTelehealth = null;
    this.servicingMet = null;
    this.servicingNotMet = null;
    this.servicingTotal = null;
    this.penetration = "---";
    this.lessThanFourStars = "---";
    this.fourStarsGreater = "---";
    this.membershipRisk = "---";
    this.membershipRiskPercent = "---";
    this.attestationPercent = "---";
    /* eslint-enable sort-keys */
  }
}

interface AdequacyAggregateDetailsState {
  columnConfig: ColumnConfig;
  comparedRecords: AdequacyAggregateWaffleToppings[];
  comparedRowGroups: Map<string, string[]>;
  displayOrder: Map<string, number>;
  rowGroups: Map<string, string[]>;
  selectedRecords: AdequacyAggregate[];
  records: AdequacyAggregateWaffleToppings[];
  templateCategories: TemplateCategory[];
}

class AdequacyAggregateDetails extends Component<AdequacyAggregateDetailsProps, AdequacyAggregateDetailsState> {
  private templateService = new TemplateService();
  private columnGroups: Array<ColumnGroup<AdequacyAggregateWaffleToppings>>;

  constructor(props: AdequacyAggregateDetailsProps) {
    super(props);

    const defaultDisplayOrder = new Map<string, number>();
    this.props.adequacyResult.aggregateDetails.forEach((ad) => defaultDisplayOrder.set(ad.key, ad.defaultDisplayOrder));

    this.state = {
      columnConfig: this.getColumnConfig(this.props.adequacyResult.aggregateDetails),
      comparedRecords: this.props.compareToRunConfiguration && this.props.compareToAdequacyResult ? this.buildWaffleToppings(this.props.compareToAdequacyResult.aggregateDetails, true) : [],
      comparedRowGroups: null,
      displayOrder: defaultDisplayOrder,
      records: this.buildWaffleToppings(this.props.adequacyResult.aggregateDetails),
      rowGroups: new Map<string, string[]>(),
      selectedRecords: [],
      templateCategories: [],
    };

    this.columnGroups = this.getColumnGroups();
  }

  public async componentDidMount() {
    if (this.props.location.state && this.props.location.state.runConfiguration) {
      const config = this.props.location.state.runConfiguration;
      const compareToConfig = this.props.location.state.compareToRunConfiguration;
      await this.props.setDualMembershipMode(this.props.location.state.isDualMembershipMode, config.projectRunConfigurationType, config.projectRunConfigurationId, compareToConfig ? compareToConfig.projectRunConfigurationId : undefined);
    }

    let templateCategories: TemplateCategory[];
    if (this.props.templateMetadata) {
      templateCategories = await this.templateService.getTemplateCategories(this.props.templateMetadata.templateCollectionId, this.props.templateMetadata.templateId);
    }

    let comparedRecords = this.buildWaffleToppings(this.props.compareToAdequacyResult ? this.props.compareToAdequacyResult.aggregateDetails : [], true);
    let records = this.buildWaffleToppings(this.props.adequacyResult.aggregateDetails);
    [records, comparedRecords] = this.reconcileComparedDifferences(records, comparedRecords);

    this.setState({
      comparedRecords,
      comparedRowGroups: this.props.compareToAdequacyResult ? this.buildRowGroups(this.props.compareToAdequacyResult.aggregateDetails, templateCategories) : null,
      records,
      rowGroups: this.buildRowGroups(this.props.adequacyResult.aggregateDetails, templateCategories),
      templateCategories,
    });
  }

  public componentDidUpdate(prevProps: AdequacyAggregateDetailsProps) {
    if (
      (
        !isEqual(prevProps.adequacyResult.aggregateDetails, this.props.adequacyResult.aggregateDetails) ||
        prevProps.module !== this.props.module ||
        (prevProps.compareToRunConfiguration && !this.props.compareToRunConfiguration)
      ) &&
      !this.props.compareToRunConfiguration ||
      prevProps.perspective !== this.props.perspective ||
      (prevProps.cmsCreditIsApplying !== this.props.cmsCreditIsApplying ||
        prevProps.cmsCreditShowOnlyChanges !== this.props.cmsCreditShowOnlyChanges)
    ) {
      this.setState({
        columnConfig: this.getColumnConfig(this.props.adequacyResult.aggregateDetails),
        comparedRecords: [],
        comparedRowGroups: this.props.compareToAdequacyResult ? this.buildRowGroups(this.props.compareToAdequacyResult.aggregateDetails, this.state.templateCategories) : null,
        records: this.buildWaffleToppings(this.props.adequacyResult.aggregateDetails),
        rowGroups: this.buildRowGroups(this.props.adequacyResult.aggregateDetails, this.state.templateCategories),
      });
    }

    if (
      this.props.compareToRunConfiguration &&
      this.props.compareToAdequacyResult &&
      (!prevProps.compareToAdequacyResult ||
        !isEqual(prevProps.adequacyResult.aggregateDetails, this.props.adequacyResult.aggregateDetails) ||
        !isEqual(prevProps.compareToAdequacyResult.aggregateDetails, this.props.compareToAdequacyResult.aggregateDetails) ||
        prevProps.perspective !== this.props.perspective || prevProps.runFrequency !== this.props.runFrequency
      )
    ) {
      let comparedRecords = this.buildWaffleToppings(this.props.compareToAdequacyResult.aggregateDetails, true);
      let records = this.buildWaffleToppings(this.props.adequacyResult.aggregateDetails);
      [records, comparedRecords] = this.reconcileComparedDifferences(records, comparedRecords);
      const defaultDisplayOrder = new Map(this.state.displayOrder);
      this.props.adequacyResult.aggregateDetails.forEach((ad) => defaultDisplayOrder.set(ad.key, ad.defaultDisplayOrder));
      // in the event of a perspective change
      const newRowGroups = this.buildRowGroups(this.props.adequacyResult.aggregateDetails, this.state.templateCategories);
      const newComparedRowGroups = this.props.compareToAdequacyResult ? this.buildRowGroups(this.props.compareToAdequacyResult.aggregateDetails, this.state.templateCategories) : null;

      this.setState({
        columnConfig: this.getColumnConfig(this.props.adequacyResult.aggregateDetails, this.props.compareToAdequacyResult.aggregateDetails),
        comparedRecords,
        comparedRowGroups: newComparedRowGroups,
        displayOrder: defaultDisplayOrder,
        records,
        rowGroups: newRowGroups,
        selectedRecords: [],
      });
    }

    if (prevProps.perspective !== this.props.perspective || !isEqual(prevProps.adequacyResult.aggregateDetails, this.props.adequacyResult.aggregateDetails) || !isEqual(prevProps.compareToAdequacyResult, this.props.compareToAdequacyResult)) {
      const defaultDisplayOrder = new Map(this.state.displayOrder);
      this.props.adequacyResult.aggregateDetails.forEach((ad) => defaultDisplayOrder.set(ad.key, ad.defaultDisplayOrder));

      this.setState({
        displayOrder: defaultDisplayOrder,
      });
    }

    if ((!!this.props.compareToRunConfiguration && !isEqual(prevProps.compareToRunConfiguration, this.props.compareToRunConfiguration))
      || (prevProps.adequacyFilters !== this.props.adequacyFilters)) {
      let comparedRecords = this.buildWaffleToppings(this.props.compareToAdequacyResult.aggregateDetails, true);
      let records = this.buildWaffleToppings(this.props.adequacyResult.aggregateDetails);
      [records, comparedRecords] = this.reconcileComparedDifferences(records, comparedRecords);
      this.setState({
        comparedRecords,
        records,
      });
    }

    if (prevProps.perspective !== this.props.perspective) {
      const comparedAggregateDetails = this.props.compareToAdequacyResult ? this.props.compareToAdequacyResult.aggregateDetails : undefined;
      this.setState({
        columnConfig: this.getColumnConfig(this.props.adequacyResult.aggregateDetails, comparedAggregateDetails),
        selectedRecords: [],
      });
    }
  }

  public render() {
    const isComparingResults = !!this.props.compareToRunConfiguration;
    const hasFailed = this.props.runConfiguration && this.props.runConfiguration.status === ProjectRunConfigurationStatus.Failed;
    let haveResultsToRender =
      ((!this.props.loading &&
        !this.props.loadingResults) ||
        this.props.project.isReadOnly) &&
      !!this.props.runConfiguration &&
      !hasFailed;

    haveResultsToRender = !isComparingResults
      ? haveResultsToRender
      : haveResultsToRender && !!this.props.compareToAdequacyResult;

    // This causes the table to unmount and remount when props change (e.g. compare mode). Might not be ideal.
    return (
      <>
        {this.props.project.showCMSCredit && this.props.cmsCreditIsApplying && cmsCreditDataAlert(this.props.cmsCreditTemplateValue)}
        {haveResultsToRender ? this.renderAdequacyAggregates() : <span className={`skeleton ${hasFailed ? "" : "shimmer"}`}><SkeletonTable /></span>}
      </>
    );
  }

  private shouldShowRatebook = () => {
    if (!this.props.runConfiguration) {
      return false;
    }
    // We only show the ratebook information in a very specific situation:
    const showRatebook = (this.props.runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.Opportunity ||
      this.props.runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.TermedOpportunity ||
      this.props.runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ProjectedOpportunity ||
      this.props.runConfiguration.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ProjectedTermedOpportunity) &&
      !this.props.regionFilters.specialty && // No way to aggregate ratebook information at this level.
      this.props.runConfiguration &&
      this.props.runConfiguration.template &&
      this.props.runConfiguration.template.useMedicareRatebook;
    return showRatebook;
  }

  private buildWaffleToppings = (records: AdequacyAggregate[], compared?: boolean) => {
    if (records.length === 0) {
      return [];
    }
    const isShared = this.props.project.resource.isShared;

    // Only show informational specialty groups if:
    // a) We're looking at specialty groups.
    // b) One of the specialty groups is marked as such.
    const showInformationalIndicator = this.props.perspective === ProjectPerspective.BySpecialty &&
      records.some(({ specialtyGroupIsInformational }) => !!specialtyGroupIsInformational);

    const existingQuery = parseQuerystring(this.props.location.search);
    const gapQuery = querystringify({
      ...existingQuery,
      all: false,
      exceptions: false,
      gaps: true,
    });
    const exceptionQuery = querystringify({
      ...existingQuery,
      all: false,
      exceptions: true,
      gaps: false,
    });

    const resultsFor: string = this.getResultsFor(!!compared);

    /* eslint-disable sort-keys */
    const waffleToppings: AdequacyAggregateWaffleToppings[] = records.map((sr) => ({
      key: `${sr.key}`,
      title: (
        <span key={`${sr.displayText}${sr.parentAbbreviation ? `, ${sr.parentAbbreviation}` : ""}`}>
          <InformationalIndicator visible={showInformationalIndicator} isInformational={!!sr.specialtyGroupIsInformational} />
          <Link
            to={{
              pathname: `${this.props.match.url}${sr.linkSuffix}`,
              search: this.props.location.search,
            }}
          >
            {`${sr.displayText}${sr.parentAbbreviation ? `, ${sr.parentAbbreviation}` : ""}`}
          </Link>
        </span>
      ),
      regionStatus: (() => {
        return (
          <Fragment key={sr.regionStatus}>
            {(isShared || null) && (
              !!sr.regionStatus && sr.regionStatus !== RegionSetStatusType.Unknown // We don't want to display the word "Unknown" to the user.
                ? sr.regionStatus
                : RegionSetStatusType.Active
            )}
            {(!isShared || null) && (
              sr.regionStatus
            )}
          </Fragment>
        );
      })(),
      membershipName: (
        !!sr.membershipName ? (
          <TooltipWrapper text={`Membership: ${sr.membershipName}`}>
            <i className="fa fa-users" />
          </TooltipWrapper>
        ) : null
      ),
      fullMembershipCount: sr.fullMembershipCount !== undefined && sr.fullMembershipCount !== null ? sr.fullMembershipCount.toString() : "---",
      resultsFor,
      countySSA: (() => {
        return sr.regionCode && sr.regionGranularity === RegionGranularity.County ? sr.regionCode : "---";
      })(),
      countyClass: (() => {
        return sr.regionSetClassification && sr.regionGranularity === RegionGranularity.County ? sr.regionSetClassification : "---";
      })(),
      adequacyIndex: (() => {
        if (sr.adequacyIndex !== null && sr.adequacyIndex !== undefined) {
          return `${sr.adequacyIndex.toFixed(1)}`;
        }
        return "---";
      })(),
      adequacyGraph: sr.fullMembershipCount === 0
        ? (
          <div style={{ minWidth: 200 }}>---
          </div>
        )
        : (
          <div style={{ minWidth: 200 }}>
            <ProgressBar
              bands={[
                {
                  color: "progress-bar-success",
                  colorType: "class",
                  label: AdequacyProgressBarLabels.MetBothCriteria,
                  outOfScale: false,
                  percent: sr.percentPassBoth,
                },
                {
                  color: "progress-bar-warning",
                  colorType: "class",
                  label: AdequacyProgressBarLabels.MetOneCriteria,
                  outOfScale: false,
                  percent: (sr.percentPassAccessOnly || 0) + sr.percentPassServicingOnly,
                },
                {
                  color: "progress-bar-danger",
                  colorType: "class",
                  label: AdequacyProgressBarLabels.MetNoCriteria,
                  outOfScale: false,
                  percent: sr.percentPassNone,
                },
                {
                  color: "progress-bar-info",
                  colorType: "class",
                  label: AdequacyProgressBarLabels.ApprovedException,
                  outOfScale: false,
                  percent: sr.percentPassByException,
                },
              ]}
              isGrayscale={!!sr.specialtyGroupIsInformational}
            />
          </div>
        ),
      gapsTotal: (
        (sr.gapCount > 0 && !this.props.cmsCreditIsApplying) || (this.props.cmsCreditIsApplying && sr.gapCountCMSCredit > 0) ? (
          <Link to={{ pathname: `${this.props.match.url}${sr.linkSuffix}`, search: gapQuery }} key={sr.gapCount}>
            {this.props.cmsCreditIsApplying ? sr.gapCountCMSCredit.toLocaleString() : sr.gapCount.toLocaleString()}
          </Link>
        ) : <span key={sr.gapCount}>{this.props.cmsCreditIsApplying ? sr.gapCountCMSCredit.toLocaleString() : sr.gapCount?.toLocaleString()}</span>
      ),
      gapsFillable: printNumberAsString(sr.fillableCount, "---"),
      gapsFillableBySingleProvider: printNumberAsString(sr.fillableBySingleProviderCount, "---"),
      gapsFillableByMultipleProviders: printNumberAsString(sr.fillableByMultipleProvidersCount, "---"),
      exceptionsTotal: printNumberAsString(sr.exceptionCount, "---"),
      exceptionsApproved: (
        <Fragment key={sr.approvedExceptionCount}>
          {(sr.approvedExceptionCount && !this.props.cmsCreditIsApplying) || (this.props.cmsCreditIsApplying && sr.approvedExceptionCountCMSCredit) ? (
            <Link to={{ pathname: `${this.props.match.url}${sr.linkSuffix}`, search: exceptionQuery }} key={sr.approvedExceptionCount}>
              {this.props.cmsCreditIsApplying ? sr.approvedExceptionCountCMSCredit : sr.approvedExceptionCount}
            </Link>
          ) : <span key={sr.approvedExceptionCount}>{this.props.cmsCreditIsApplying ? sr.approvedExceptionCountCMSCredit : sr.approvedExceptionCount}</span>}
        </Fragment>
      ),
      fullMemberCountWithAccess: printNumberAsString(sr.fullMemberCountWithAccess, "---"),
      fullMemberCountWithoutAccess: printNumberAsString(sr.fullMemberCountWithoutAccess, "---"),
      accessMet: printNumberAsString(this.props.cmsCreditIsApplying ? sr.accessMetCountCMSCredit : sr.accessMetCount, "---"),
      accessNotMet: printNumberAsString(this.props.cmsCreditIsApplying ? (sr.accessTotalCount - sr.accessMetCountCMSCredit) : sr.accessNotMetCount, "---"),
      accessTotal: printNumberAsString(sr.accessTotalCount, "---"),
      isTelehealth: sr.isTelehealth,
      servicingMet: printNumberAsString(sr.servicingMetCount, "---"),
      servicingNotMet: printNumberAsString(sr.servicingNotMetCount, "---"),
      servicingTotal: printNumberAsString(sr.servicingTotalCount, "---"),
      penetration: (() => {
        return sr.penetration !== null && sr.penetration !== undefined
          ? `${sr.penetration.toFixed(1)}%`
          : "---";
      })(),
      lessThanFourStars: (() => {
        return sr.lessThanFourStarsAnnually !== null && sr.lessThanFourStarsAnnually !== undefined
          ? sr.lessThanFourStarsAnnually.toLocaleString("en-US", { style: "currency", currency: "USD" })
          : "---";
      })(),
      fourStarsGreater: (() => {
        return sr.fourStarsGreaterAnnually !== null && sr.fourStarsGreaterAnnually !== undefined
          ? sr.fourStarsGreaterAnnually.toLocaleString("en-US", { style: "currency", currency: "USD" })
          : "---";
      })(),
      membershipRisk: printNumberAsString(sr.membershipRiskCount, "---"),
      membershipRiskPercent: sr.percentMembershipRisk !== null && sr.percentMembershipRisk !== undefined ? (sr.percentMembershipRisk).toFixed(1) : "---",
      attestationPercent: (() => {
        return sr && sr.recentlyAttestedUniqueNPIPercent !== undefined && sr.recentlyAttestedUniqueNPIPercent !== null
          ? sr.recentlyAttestedUniqueNPIPercent.toFixed(1)
          : "---";
      })(),
    }));
    /* eslint-enable sort-keys */

    return waffleToppings;
  }

  private reconcileComparedDifferences = (newRecords: AdequacyAggregateWaffleToppings[], comparedRecords: AdequacyAggregateWaffleToppings[]) => {
    if (!this.props.compareToRunConfiguration) {
      return [newRecords, []];
    }
    const selectedResultsFor = this.getResultsFor(false);
    const comparedResultsFor = this.getResultsFor(true);
    const getKeys = (records: AdequacyAggregateWaffleToppings[]) => {
      return records.reduce((result, current) => {
        result.push(current.key);
        return result;
      }, []);
    };
    const sortResult = (records: AdequacyAggregateWaffleToppings[], order: Map<string, number>) => {
      return records.sort((a, b) => {
        const aDisplayOrder = order.get(a.key);
        const bDisplayOrder = order.get(b.key);
        return aDisplayOrder > bDisplayOrder ? 1 : -1;
      });
    };
    const selectedKeys = getKeys(newRecords);
    const comparedKeys = getKeys(comparedRecords);
    const combinedKeys = [...selectedKeys.concat(comparedKeys)];
    const defaultOrder = new Map<string, number>();
    combinedKeys.forEach((key) => {
      if (!selectedKeys.includes(key)) {
        const blankRecord = new AdequacyAggregateWaffleToppings(key, selectedResultsFor);
        const pairedRecord = comparedRecords.find((d) => key === d.key);
        blankRecord.title = <span key={pairedRecord.key}>{pairedRecord.title}</span>;
        newRecords.push(blankRecord);

        defaultOrder.set(key, this.props.compareToAdequacyResult.aggregateDetails.find((r) => r.key === key).defaultDisplayOrder);
      }
      if (!comparedKeys.includes(key)) {
        const blankRecord = new AdequacyAggregateWaffleToppings(key, comparedResultsFor);
        const pairedRecord = newRecords.find((d) => key === d.key);
        blankRecord.title = <span key={pairedRecord.key}>{pairedRecord.title}</span>;
        comparedRecords.push(blankRecord);

        defaultOrder.set(key, this.props.adequacyResult.aggregateDetails.find((r) => r.key === key).defaultDisplayOrder);
      }
      if (!defaultOrder.get(key)) {
        defaultOrder.set(key, this.props.adequacyResult.aggregateDetails.find((r) => r.key === key).defaultDisplayOrder);
      }
    });

    return [sortResult(newRecords, defaultOrder), sortResult(comparedRecords, defaultOrder)];
  }

  private getResultsFor = (compared: boolean) => {
    if (!!this.props.compareToRunConfiguration) {
      if (this.props.isDualMembershipMode) {
        if (compared) {
          return this.props.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === this.props.runConfiguration.companionProjectRunConfigurationId).memberSetAlias;
        } else {
          return this.props.runConfiguration.memberSetAlias;
        }
      } else {
        return compared ? getTimeString(this.props.compareToRunConfiguration, this.props.runFrequency) : getTimeString(this.props.runConfiguration, this.props.runFrequency);
      }
    }
  }

  private getBulkActionBar = () => {
    const isShared = this.props.project.resource.isShared;

    let networkTypes = this.props.runConfiguration && Array.from(this.props.runConfiguration.networkTypes) || undefined;
    //remove market from available network types if project doesn't used market for impact
    if (this.props.runConfiguration && !this.props.runConfiguration.useMarketProvidersForImpact) {
      networkTypes = networkTypes.filter((nt) => nt !== "market");
    }

    let specialtyGroupCode: string = null;
    let stateRegionCode: string = null;

    if (this.props.regionFilters) {
      specialtyGroupCode = this.props.regionFilters.specialty;
      stateRegionCode = this.props.regionFilters.state;
    }

    let disableRegionFilters = false;
    let selectedRegionSetIds = this.props.perspective !== ProjectPerspective.BySpecialty
      ? uniq(this.state.selectedRecords.map((r) => r.regionSetId))
      : [];
    // if no records are selected selected, use the current state region set id as the "default" selection for export properties
    if (selectedRegionSetIds.length === 0) {
      if (stateRegionCode) {
        let stateRegion = this.props.runConfiguration.getState(stateRegionCode);
        if (!stateRegion && this.props.compareToRunConfiguration) {
          stateRegion = this.props.compareToRunConfiguration.getState(stateRegionCode);
        }
        selectedRegionSetIds = stateRegion ? [stateRegion.regionSetId] : [];
      }
    } else {
      disableRegionFilters = true;
    }

    let disableSpecialtyGroupFilters = false;
    let selectedSpecialtyGroupCodes = [ProjectPerspective.BySpecialty, ProjectPerspective.All].includes(this.props.perspective)
      ? this.state.selectedRecords.map((r) => r.specialtyGroupCode).filter((s) => !!s)
      : [];
    if (selectedSpecialtyGroupCodes.length === 0) {
      if (specialtyGroupCode) {
        selectedSpecialtyGroupCodes = [specialtyGroupCode];
      }
    }
    if (selectedSpecialtyGroupCodes.length > 0) {
      disableSpecialtyGroupFilters = true;
    }
    const availableExportTypes = this.props.perspective === ProjectPerspective.ByCounty || this.props.perspective === ProjectPerspective.ByState ?
      [ExportType.RegionAggregate, ExportType.SpecialtyAggregate, ExportType.ServicingProviders] :
      [ExportType.AllSpecialties, ExportType.SpecialtySummaryByRegion, ExportType.ServicingProviders];

    if (this.props.runConfiguration.hasZipSummaries ?? false) {
      availableExportTypes.push(ExportType.ZipSummary);
    }
    if (this.props.project.showClosestProviderReport && !this.props.compareToRunConfiguration) {
      availableExportTypes.push(ExportType.ClosestProvider);
    }

    let defaultReportType: ReportType;
    if (this.props.perspective === ProjectPerspective.ByCounty)
      defaultReportType = ReportType.CountySummary;
    else if (this.props.perspective === ProjectPerspective.ByState)
      defaultReportType = ReportType.StateSummary;
    else if (this.props.perspective === ProjectPerspective.BySpecialty)
      defaultReportType = ReportType.AdequacyAggregate;
    else
      defaultReportType = ReportType.AdequacyServicingProvider;

    const showExpandableSpecialties = this.props.adequacyResult.hasExpandableSpecialties;
    const showNextClosestProviders = this.props.runConfiguration && this.props.runConfiguration.showNextClosestNetworkProviders || false;
    const showPatternOfCareCheckbox = showPatternOfCare(this.props.user, this.props.enabledFeatureFlags, !!this.props.compareToRunConfiguration, this.props.project, this.props.runConfiguration, this.props.adequacyResult.aggregateDetails, undefined, this.props.cmsCreditIsApplying);
    const enableMembershipTypeOptions = this.props.enabledFeatureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT) &&
      this.props.runConfiguration && [ProjectRunConfigurationType.DualMembershipPrimary, ProjectRunConfigurationType.DualMembershipSupplemental].includes(this.props.runConfiguration.projectRunConfigurationType);

    const { cmsCreditIsApplying, cmsCreditShowOnlyChanges, cmsCreditTemplateValue, setCMSCreditIsApplying, setCMSCreditShowOnlyChanges } = this.props;
    const selectedMemberships: number[] = [this.props.runConfiguration.memberSetId];
    if (this.props.isDualMembershipMode) {
      selectedMemberships.push(this.props.compareToRunConfiguration.memberSetId);
    }

    return (
      <span className="bulk-action-group">
        <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              // old exportdata
              activeModule: this.props.module,
              adequacyFilters: this.props.adequacyFilters,
              availableExportTypes,
              comparedRunConfigurationId: this.props.exportComparedRunConfigurationId,
              disableRegionFilters,
              disableSpecialtyGroupFilters,
              enableMembershipTypeOptions: enableMembershipTypeOptions,
              filters: this.props.filters,
              granularity: this.getRegionGranularity(),
              initialNetworkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              networkTypes: networkTypes as ProviderNetworkType[],
              nextClosestProviders: showNextClosestProviders,
              perspective: this.props.perspective,
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.location.pathname + this.props.location.search,
              regionSetIds: selectedRegionSetIds,
              showAdvancedComparison: this.props.project.showAdvancedComparison,
              showAppendNextClosestProviders: showNextClosestProviders,
              showAppendPatternOfCareProviders: showPatternOfCareCheckbox,
              showExpandableSpecialties: showExpandableSpecialties,
              specialtyGroupCodes: selectedSpecialtyGroupCodes,

              // new reportcustomization
              /* eslint-disable sort-keys */
              projectName: this.props.project.resource.name,
              networkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              selectedRegionSetIds: this.state.selectedRecords.map((sr) => sr.regionSetId),
              selectedSpecialties: this.state.selectedRecords.map((sr) => sr.specialtyGroupCode),
              defaultReportType,
              primaryMembership: { alias: this.props.project.memberSetAlias || this.props.project.memberSetName, id: this.props.project.memberSetId },
              secondaryMembership: { alias: this.props.project.memberSetAlias2 || this.props.project.memberSetName2, id: this.props.project.memberSetId2 },
              selectedMemberships,
              selectedTimeslice: this.props.runConfiguration.projectRunConfigurationId, // runConfigurationId
              isCompareMode: !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode,
              selectedComparedTimeslice: !this.props.isDualMembershipMode ? this.props.compareToRunConfiguration?.projectRunConfigurationId : null, // compareToRunConfigurationId
              isViewingOpportunity: this.props.adequacyFilters.opportunity,
              showAddedProviders: !!this.props.compareToRunConfiguration,
              showMatchedProviders: !!this.props.compareToRunConfiguration,
              showRemovedProviders: !!this.props.compareToRunConfiguration,
              showPatternOfCare: this.props.project.showPatternOfCareProviders,
              hasProspectProviders: !!this.props.runConfiguration.prospectProviderSetId,
              hasMarketProviders: !!this.props.runConfiguration.marketProviderSetId,
              hasNextClosestProviders: !!this.props.runConfiguration.showNextClosestNetworkProviders,
              showZipSummariesReport: this.props.project.hasZipSummaries,
              /* eslint-enable */
            },
          }}
          data-testid="adequacy-aggregate-details__create-report-button"
        >
          <i className="fa fa-cloud-download" /> Create Report
        </Link>
        {!isShared && <ChangeStatusLink regions={this.state.selectedRecords} {...this.props} />}
        {!this.props.compareToRunConfiguration && this.props.project.showCMSCredit && cmsCreditIconToggleApply(cmsCreditIsApplying, setCMSCreditIsApplying, cmsCreditTemplateValue)}
        {!this.props.compareToRunConfiguration && this.props.project.showCMSCredit && cmsCreditIconToggleChanges(cmsCreditIsApplying, cmsCreditShowOnlyChanges, setCMSCreditShowOnlyChanges, cmsCreditTemplateValue, this.props.adequacyFilters.gaps, (Boolean)(this.props.regionFilters.county) && (Boolean)(this.props.regionFilters.specialty))}
      </span>);
  }

  private getLeadColumnTitle() {
    const perspective = this.props.perspective;
    const leadColumnTitle = perspective === ProjectPerspective.ByState ?
      "State" :
      perspective === ProjectPerspective.ByCounty ?
        "County" :
        perspective === ProjectPerspective.BySpecialty ?
          "Specialty Group" :
          "";
    return leadColumnTitle;
  }

  private renderAdequacyAggregates() {
    const selectedKeys: string[] = [...new Set(this.state.selectedRecords.map((sr: AdequacyAggregate) => sr.key))];
    const runConfigurationDate = moment(this.props.runConfiguration.dateCreated);
    const compareToRunConfigurationDate = this.props.compareToRunConfiguration ? moment(this.props.compareToRunConfiguration.dateCreated) : null;
    const isCompareOlder = this.props.compareToRunConfiguration ? compareToRunConfigurationDate.isBefore(runConfigurationDate) : false;

    let data;
    let compareData;
    if (this.props.compareToRunConfiguration && isCompareOlder) {
      data = this.state.comparedRecords;
      compareData = this.state.records.length ? this.state.records : null;
    } else if (this.props.compareToRunConfiguration && !isCompareOlder && this.state.comparedRecords) {
      data = this.state.records;
      compareData = this.state.comparedRecords.length ? this.state.comparedRecords : null;
    } else {
      data = this.state.records;
      compareData = null;
    }

    return (
      <Waffles<AdequacyAggregateWaffleToppings>
        bulkActionBar={this.getBulkActionBar()}
        columns={this.state.columnConfig}
        columnGroups={this.columnGroups}
        compareData={compareData && compareData.length ? compareData : null}
        compareRowGroups={this.props.compareToRunConfiguration && isCompareOlder ? this.state.rowGroups : this.state.comparedRowGroups}
        customSort={this.customSort}
        data={data || []}
        defaultDisplayOrder={this.state.displayOrder}
        fullWidth={true}
        handleCheckboxChange={this.handleCheckboxChange}
        //onFilterChange={this.filterRecords}
        rowGroups={this.props.compareToRunConfiguration && isCompareOlder ? this.state.comparedRowGroups : this.state.rowGroups}
        selectedKeys={selectedKeys}
        showCheckboxes={true}
        testId="adequacy-aggregate-details"
      //virtualize={{
      //  rowHeight: 48,
      //  viewportHeight: 1000,
      //}}
      />
    );
  }

  private handleCheckboxChange = (selectedKeys: string[]) => {
    let data = [...this.props.adequacyResult.aggregateDetails];
    if (!!this.props.compareToRunConfiguration) {
      data = data.concat(this.props.compareToAdequacyResult.aggregateDetails);
    }

    const selectedData = data.filter((result) => selectedKeys.includes(`${result.key}`));
    this.setState({
      selectedRecords: selectedData,
    });
  }

  private getRegionGranularity = () => {
    // if a county is selected or a state is selected, report at county granularity
    if (this.props.regionFilters.county || this.props.regionFilters.state || this.props.perspective === ProjectPerspective.ByCounty) {
      return RegionGranularity.County;
    }
    // if nothing is selected, the granularity is state
    return RegionGranularity.State;
  }

  private customSort = (records: AdequacyAggregateWaffleToppings[], column: keyof AdequacyAggregateWaffleToppings, direction: string) => {
    const data = [...records];
    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    switch (column) {
      case "resultsFor":
      case "countySSA":
      case "countyClass":
      case "isTelehealth":
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      case "title":
      case "regionStatus":
        return data.sort(
          (a, b) => {
            return (a[column].key > b[column].key ? 1 : -1) * multiplier;
          });
      case "gapsTotal":
      case "exceptionsApproved":
        return numberSort(records, column, multiplier, (item) => (item as JSX.Element).key as string);
      case "adequacyIndex":
      case "adequacyGraph":
        return numberSort<AdequacyAggregateWaffleToppings>(records, "adequacyIndex", multiplier);
      case "membershipRisk":
      case "membershipRiskPercent":
      case "gapsFillable":
      case "gapsFillableByMultipleProviders":
      case "gapsFillableBySingleProvider":
      case "exceptionsTotal":
      case "accessNotMet":
      case "accessTotal":
      case "servicingMet":
      case "servicingNotMet":
      case "servicingTotal":
      case "attestationPercent":
      case "lessThanFourStars":
      case "fourStarsGreater":
      case "penetration":
      case "accessMet":
      case "fullMembershipCount":
      case "fullMemberCountWithAccess":
      case "fullMemberCountWithoutAccess":
        return numberSort(records, column, multiplier);
      default:
        console.warn(`No sort definition for column ${column}.`);
        return records;
    }
  }

  private buildRowGroups = (sortedRecords: AdequacyAggregate[], templateCategories: TemplateCategory[]) => {
    if (this.props.perspective !== ProjectPerspective.BySpecialty || !sortedRecords) {
      return null;
    }

    const rowGroups = new Map<string, string[]>();
    templateCategories.sort((a, b) => a.displayOrder - b.displayOrder);
    templateCategories.forEach((cat, index) => {
      const key = cat.displayName || `Unnamed Category ${index + 1}`;
      rowGroups.set(key, sortedRecords.filter((sr) => cat.specialtyGroupCodes.includes(sr.specialtyGroupCode)).map((filteredRecord) => filteredRecord.key));
    });
    return rowGroups;
  }

  private getColumnConfig = (records: AdequacyAggregate[], compareToRecords?: AdequacyAggregate[]) => {
    const leadColumnTitle = this.getLeadColumnTitle();
    const showRatebook = this.shouldShowRatebook();
    const userHasEnhancedFillableGaps = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);

    // The order of these columns must match the order of property assignments in the
    // AdequacyAggregateWaffleToppings constructor for compare mode to display properly.
    const columnConfig: ColumnConfig = {
      accessMet: {
        displayText: "Met",
        filterText: "Access: Met",
        filterType: WafflesFilterType.number,
        testId: "access-met",
      },
      accessNotMet: {
        displayText: "Not Met",
        filterText: "Access: Not Met",
        filterType: WafflesFilterType.number,
        testId: "access-not-met",
      },
      accessTotal: {
        displayText: "Total",
        filterText: "Access: Total",
        filterType: WafflesFilterType.number,
        testId: "access-total",
      },
      adequacyGraph: {
        displayText: "Adequacy",
        minWidth: "200px",
        noFilter: true,
        testId: "adequacy-graph",
      },
      adequacyIndex: {
        displayText: "Adequacy Index",
        filterType: WafflesFilterType.number,
        testId: "adequacy-index",
      },
      attestationPercent: {
        displayText: "Attestation %",
        filterType: WafflesFilterType.number,
        hidden: !this.props.project.showAttestation,
        testId: "attestation-percent",
      },
      countyClass: {
        displayText: "County Class",
        filterType: WafflesFilterType.string,
        hidden: !records.some((r) => r.regionCode && r.regionGranularity === RegionGranularity.County) && !(compareToRecords || []).some((cr) => cr.regionCode && cr.regionGranularity === RegionGranularity.County),
        testId: "county-class",
      },
      countySSA: {
        displayText: "County SSA",
        filterType: WafflesFilterType.string,
        hidden: !records.some((r) => r.regionCode && r.regionGranularity === RegionGranularity.County) && !(compareToRecords || []).some((cr) => cr.regionCode && cr.regionGranularity === RegionGranularity.County),
        testId: "county-ssa",
      },
      exceptionsApproved: {
        displayText: "Approved",
        filterText: "Exceptions: Approved",
        filterType: WafflesFilterType.number,
        testId: "exceptions-approved",
      },
      exceptionsTotal: {
        displayText: "Total",
        filterText: "Exceptions: Total",
        filterType: WafflesFilterType.number,
        testId: "exceptions-total",
      },
      fourStarsGreater: {
        displayText: "4 Star + Annual",
        filterText: "Opportunities: 4 Star + Annual",
        filterType: WafflesFilterType.number,
        hidden: !showRatebook,
        testId: "four-stars-greater",
      },
      fullMemberCountWithAccess: {
        displayText: "# of Members With ",
        filterType: WafflesFilterType.number,
        hidden: !(this.props.perspective === ProjectPerspective.BySpecialty),
        testId: "membersCount-with",
      },
      fullMemberCountWithoutAccess: {
        displayText: "# of Members Without ",
        filterType: WafflesFilterType.number,
        hidden: !(this.props.perspective === ProjectPerspective.BySpecialty),
        testId: "membersCount-without",
      },
      fullMembershipCount: {
        displayText: "Membership Count",
        hidden: this.props.project.editProjectOverrideMemberSets.length != 0 && (this.props.perspective == ProjectPerspective.ByState || this.props.perspective == ProjectPerspective.ByCounty),
        testId: "membership-count",
      },
      gapsFillable: {
        displayText: "Fillable",
        filterText: "Gaps: Fillable",
        filterType: WafflesFilterType.number,
        hidden: this.props.cmsCreditIsApplying || !userHasEnhancedFillableGaps,
        testId: "gaps-fillable",
      },
      gapsFillableByMultipleProviders: {
        displayText: "Fillable by > 1 Provider",
        filterText: "Gaps: Fillable",
        filterType: WafflesFilterType.number,
        hidden: this.props.cmsCreditIsApplying || !userHasEnhancedFillableGaps,
        testId: "gaps-fillable-by-multiple-providers",
        tooltip: "Count of the unique gaps that are fillable by adding a combination of more than 1 provider",
      },
      gapsFillableBySingleProvider: {
        displayText: "Fillable by Single Provider",
        filterText: "Gaps: Fillable",
        filterType: WafflesFilterType.number,
        hidden: this.props.cmsCreditIsApplying,
        testId: "gaps-fillable-by-single-provider",
      },
      gapsTotal: {
        displayText: "Total",
        filterText: "Gaps: Total",
        filterType: WafflesFilterType.number,
        testId: "gaps-total",
      },
      isTelehealth: {
        displayText: "Telehealth",
        hidden: this.props.perspective !== ProjectPerspective.BySpecialty || !this.props.runConfiguration || (this.props.runConfiguration.telehealthSpecialtyGroupCodes.length === 0 && (!this.props.compareToRunConfiguration || this.props.compareToRunConfiguration.telehealthSpecialtyGroupCodes.length === 0)),
        noFilter: true,
        testId: "is-telehealth",
      },
      lessThanFourStars: {
        displayText: "< 4 Star Annual",
        filterText: "Opportunities: < 4 Star Annual",
        filterType: WafflesFilterType.number,
        hidden: !showRatebook,
        testId: "less-than-four-stars",
      },
      membershipName: {
        alignment: TextAlignment.Center,
        displayElement: <i className="fa fa-users" />,
        displayText: "Membership",
        hidden: this.props.perspective !== ProjectPerspective.BySpecialty || (!this.props.adequacyResult || this.props.adequacyResult.aggregateDetails.every((r) => !r.membershipName)),
        noSort: true,
        testId: "membershipName",
      },
      membershipRisk: {
        displayText: "#",
        filterText: "Membership At Risk #",
        filterType: WafflesFilterType.number,
        hidden: !this.props.user.roles.includes(Roles.MEMBERSHIP_RISK_USER) || this.props.perspective === ProjectPerspective.BySpecialty,
        testId: "membership-risk",
      },
      membershipRiskPercent: {
        displayText: "%",
        filterText: "Membership At Risk %",
        filterType: WafflesFilterType.number,
        hidden: !this.props.user.roles.includes(Roles.MEMBERSHIP_RISK_USER) || this.props.perspective === ProjectPerspective.BySpecialty,
        testId: "membership-risk-percent",
      },
      penetration: {
        displayText: "Penetration",
        filterText: "Opportunities: Penetration",
        filterType: WafflesFilterType.number,
        hidden: !showRatebook,
        testId: "penetration",
      },
      regionStatus: {
        displayText: "Status",
        hidden: this.props.perspective !== ProjectPerspective.ByCounty,
        noFilter: true,
        testId: "region-status",
      },
      resultsFor: {
        displayText: "Results For",
        filterType: WafflesFilterType.boolean,
        hidden: !this.props.isDualMembershipMode && !this.props.compareToRunConfiguration,
        noSort: true,
        sticky: true,
        testId: "results-for",
      },
      servicingMet: {
        displayText: "Met",
        filterText: "Servicing: Met",
        filterType: WafflesFilterType.number,
        testId: "servicing-met",
      },
      servicingNotMet: {
        displayText: "Not Met",
        filterText: "Servicing: Not Met",
        filterType: WafflesFilterType.number,
        testId: "servicing-not-met",
      },
      servicingTotal: {
        displayText: "Total",
        filterText: "Servicing: Total",
        filterType: WafflesFilterType.number,
        testId: "servicing-total",
      },
      title: {
        displayText: leadColumnTitle,
        filterType: WafflesFilterType.string,
        sticky: true,
        testId: "title",
      },
    };

    return columnConfig;
  }

  private filterRecords = (row: AdequacyAggregateWaffleToppings, columnFilter: ColumnFilter) => {
    const colKey = columnFilter.key;
    if (colKey === "title") {
      // type: string, but it's an element. That's a special case.
      switch (columnFilter.value.operator) {
        case "contains":
          return (row.title.key as string).toLowerCase().includes((columnFilter.value.value as string).toLowerCase());
        case "does not contain":
          return !(row.title.key as string).toLowerCase().includes((columnFilter.value.value as string).toLowerCase());
      }
    } else if (colKey === "gapsTotal" || colKey === "exceptionsApproved") {
      // type: number, but as an element.
      const specialString = row[colKey].key as string;
      switch (columnFilter.value.operator) {
        case "greater than":
          // removing the commas here only works with the US number system, but fortunately, I'm pretty sure that's all we're dealing with
          return parseNumber(specialString) > parseNumber((columnFilter.value.value as string));
        case "equal to":
          return parseNumber(specialString) === parseNumber((columnFilter.value.value as string));
        case "less than":
          return parseNumber(specialString) < parseNumber((columnFilter.value.value as string));
      }
    }

    return genericFilter<AdequacyAggregateWaffleToppings>(row, colKey, columnFilter);
  }

  private getColumnGroups = () => {
    const columnGroups: Array<ColumnGroup<AdequacyAggregateWaffleToppings>> = [
      {
        children: ["title", "resultsFor", "regionStatus", "countySSA", "countyClass", "membershipName", "fullMembershipCount"],
        hideKey: true,
        key: "id",
        testId: "group-id",
      },
      {
        children: ["adequacyIndex", "adequacyGraph"],
        hideKey: true,
        key: "adequacy",
        testId: "group-adequacy",
      },
      {
        children: ["gapsTotal", "gapsFillable", "gapsFillableBySingleProvider", "gapsFillableByMultipleProviders"],
        key: "Gaps",
        testId: "group-gaps",
      },
      {
        children: ["exceptionsTotal", "exceptionsApproved"],
        key: "Exceptions",
        testId: "group-exceptions",
      },
      {
        children: ["fullMemberCountWithAccess", "fullMemberCountWithoutAccess", "accessMet", "accessNotMet", "accessTotal", "isTelehealth"],
        key: "Access",
        testId: "group-access",
      },
      {
        children: ["servicingMet", "servicingNotMet", "servicingTotal"],
        key: "Servicing",
        testId: "group-servicing",
      },
      {
        children: ["penetration", "lessThanFourStars", "fourStarsGreater"],
        key: "Opportunities",
        testId: "group-opportunities",
      },
      {
        children: ["membershipRisk", "membershipRiskPercent"],
        key: "Membership at Risk",
        testId: "group-membership-risk",
      },
      {
        children: ["attestationPercent"],
        hideKey: true,
        key: "attestation-region",
        testId: "group-attestation",
      },
    ];

    return columnGroups;
  }
}

const WrappedAdequacyAggregateDetails = withFeatureFlagContext(withCountyProjectContext(withUserContext(AdequacyAggregateDetails)));

export { WrappedAdequacyAggregateDetails as AdequacyAggregateDetails };
